import type { WikiLink } from "./types.js";

/**
 * Match a single wiki link including optional anchor and alias.
 *
 * `[[name]]` / `[[name|alias]]` / `[[name#anchor]]` / `[[name#anchor|alias]]`
 *
 * The name part disallows `|`, `#`, and `]` so the components separate
 * cleanly even when several links appear on one line.
 */
const WIKI_LINK_PATTERN = /\[\[([^\]|#\n]+?)(?:#([^\]|\n]+?))?(?:\|([^\]\n]+?))?\]\]/g;

/**
 * Extract every wiki link from a markdown body. Returns links in
 * document order; duplicates are preserved (callers can dedupe if they
 * want, but a broken link in two places is still two repairs).
 *
 * Code fences and inline code spans are not stripped — this module
 * treats markdown as text. Hosts that need to ignore links inside
 * code blocks should pre-filter the body.
 */
export function extractWikiLinks(body: string): readonly WikiLink[] {
  const out: WikiLink[] = [];
  for (const match of body.matchAll(WIKI_LINK_PATTERN)) {
    const name = match[1]?.trim();
    if (!name) continue;
    const anchor = match[2]?.trim();
    const alias = match[3]?.trim();
    out.push({
      name,
      anchor: anchor || undefined,
      alias: alias || undefined,
      raw: match[0],
    });
  }
  return out;
}
